import { Edit } from '@mui/icons-material'
import { Box, IconButton, Stack } from '@mui/material'
import React, { useCallback, useContext, useState } from 'react'
import { SubmitHandler } from 'react-hook-form'

import { AuthContext } from '../../providers'
import { useUpdateCustomerDetailMutation } from '../../redux/api'
import { CustomerEntry, CustomerInformation } from '../../sdk'
import { loggingAssert } from '../../utils/handleError/loggingAssert'
import { NewCustomerForm } from '../Customers'
import { AdditionalEmailAddressesTable } from './AdditionalEmailAddressesTable/AdditionalEmailAddressesTable'
import { AdditionalPhonesTable } from './AdditionalPhonesTable/AdditionalPhonesTable'
import { CustomerDetailPanel } from './CustomerDetailPanel'

interface Props {
  customer: CustomerEntry
}

export const CustomerDetailForm: React.FC<Props> = ({ customer }) => {
  const { currentUser, userShopId } = useContext(AuthContext)
  const shopId = loggingAssert('ShopId not set', userShopId)
  const accessToken = loggingAssert('Token not set', currentUser!.accessToken)
  const id = loggingAssert('CustomerId not set', customer.id)
  loggingAssert('Customer information not set', customer.information)
  const [updateCustomer] = useUpdateCustomerDetailMutation()
  const [editing, setEditing] = useState(false)

  const onSubmit: SubmitHandler<CustomerInformation> = useCallback(
    (data: CustomerInformation) => {
      updateCustomer({
        customerEntry: {
          id,
          information: {
            shop: shopId,
            ...data,
            defaultPriceLevel: data.defaultPriceLevel,
            defaultLaborMatrix: data.defaultLaborMatrix,
            defaultPartsMatrix: data.defaultPartsMatrix
          }
        },
        authorization: accessToken
      })
      setEditing(false)
    },
    [accessToken, id, shopId, updateCustomer]
  )
  return (
    <Stack
      direction="row"
      justifyContent="space-between"
      alignItems="flex-start"
      spacing={3}>
      <Box sx={{ width: '100%' }}>
        {!editing && (
          <CustomerDetailPanel customerInfo={customer.information!} />
        )}
        {editing && (
          <NewCustomerForm
            onSubmit={onSubmit}
            defaultValues={customer.information!}
            cancelCallback={() => {
              setEditing(false)
            }}
          />
        )}
        <AdditionalPhonesTable customerEntry={customer} />
        <AdditionalEmailAddressesTable customerEntry={customer} />
      </Box>
      <Box>
        {!editing && (
          <IconButton
            onClick={() => setEditing(true)}
            aria-label="editCustomerButton">
            <Edit />
          </IconButton>
        )}
      </Box>
    </Stack>
  )
}
